export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface Logger {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
}

class AppLogger implements Logger {
  private logLevel: LogLevel = LogLevel.INFO;

  constructor() {
    this.logLevel = this.resolveInitialLevel();
  }

  private resolveInitialLevel(): LogLevel {
    const configuredLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
    const debugLogsEnabled =
      import.meta.env.VITE_DEBUG_LOGS === "true" ||
      (typeof window !== "undefined" &&
        window.localStorage.getItem("terramatrix_debug_logs") === "true");

    switch (configuredLevel) {
      case "debug":
        return LogLevel.DEBUG;
      case "info":
        return LogLevel.INFO;
      case "warn":
        return LogLevel.WARN;
      case "error":
        return LogLevel.ERROR;
      default:
        break;
    }

    if (import.meta.env.MODE === "production") {
      return LogLevel.WARN;
    }

    return debugLogsEnabled ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level}] ${message}`;
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage("DEBUG", message), ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage("INFO", message), ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage("WARN", message), ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage("ERROR", message), ...args);
    }
  }
}

export const logger = new AppLogger();
